import {
  API,
  SPECTATOR_API,
  WS_PATH,
  type Handicap,
  type RedCount,
  type BallColor,
  type MatchSnapshot,
  type MatchDevices,
  type MatchLiveViewers,
  type NewMatchEvent,
  type ScoreState,
  type ServerMessage,
  type SpectatorPage,
} from '@aiscoreboard/contracts';
import { controlToken } from './operator.ts';
import { devicePairingHeaders, preferredPairing } from './devicePairing.ts';
import { loadStoredMatchId, MATCH_STORAGE_KEY } from './matchStorage.ts';
import { withRequestTimeout } from './requestTimeout.ts';
import { remoteControlHeaders } from './remoteControl.ts';

function operatorHeaders(): Record<string, string> {
  const token = controlToken();
  return { ...(token ? { authorization: `Bearer ${token}` } : {}), ...devicePairingHeaders() };
}

/**
 * A failed request, with the status kept alongside the message.
 *
 * The message used to be built as `409 Conflict {"error":"nothing to undo"}`
 * and put on screen exactly like that, because a refusal and a crash came back
 * through the same channel with no way to tell them apart. Some refusals are
 * *expected* — pressing Undo with nothing to undo is a thing a person will do
 * several times a night, and answering it with an HTTP status and a fragment
 * of JSON tells them the app is broken when nothing is wrong. Carrying the
 * status lets a call site recognise its own expected refusals and say
 * something a player can act on, while everything genuinely unexpected still
 * surfaces in full.
 */
export class ApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

export async function fetchSpectatorMatches(signal: AbortSignal): Promise<SpectatorPage> {
  return withRequestTimeout(10_000, async requestSignal => json<SpectatorPage>(await fetch(SPECTATOR_API, {
    cache: 'no-store', credentials: 'omit',
    signal: requestSignal,
  })), signal);
}

export async function fetchMatchDevices(matchId: string, signal?: AbortSignal): Promise<MatchDevices> {
  return withRequestTimeout(10_000, async requestSignal => json<MatchDevices>(await fetch(`/api/matches/${encodeURIComponent(matchId)}/devices`, {
    cache: 'no-store', credentials: 'omit',
    headers: operatorHeaders(),
    signal: requestSignal,
  })), signal);
}

export async function fetchLiveViewers(matchId: string, signal: AbortSignal): Promise<MatchLiveViewers> {
  return withRequestTimeout(20_000, async requestSignal => json<MatchLiveViewers>(await fetch(
    API.liveViewers(encodeURIComponent(matchId)),
    { cache: 'no-store', credentials: 'omit', signal: requestSignal },
  )), signal);
}

async function json<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    // The server answers errors as `{ "error": "..." }`. Unwrap it when it
    // does, so the message is the sentence it wrote rather than the envelope.
    let message = '';
    try {
      const parsed = JSON.parse(detail) as { error?: unknown };
      if (typeof parsed.error === 'string') message = parsed.error;
    } catch {
      // Not JSON — a proxy's HTML error page, or nothing at all. Don't show
      // raw HTML to the user; fall through to the status-based fallback.
    }
    throw new ApiError(
      response.status,
      message || `${response.status} ${response.statusText}`.trim(),
    );
  }
  return (await response.json()) as T;
}

/**
 * A unique id for one attempt at one write.
 *
 * It cannot be `crypto.randomUUID()`. That method is restricted to secure
 * contexts, and the one device that most needs it — a phone that has just
 * scanned the QR code — reaches the scoreboard at `http://192.168.x.x:5173`,
 * because a venue laptop has no certificate for its LAN address. On that
 * phone `crypto.randomUUID` is simply not there, and every button on the
 * remote threw `crypto.randomUUID is not a function` before the request was
 * ever sent.
 *
 * `crypto.getRandomValues` is *not* secure-context-only and is in every
 * browser this app supports, so the v4 layout is assembled by hand from it.
 * The last resort covers nothing left standing, but costs four lines: the id
 * only has to be unique among one client's in-flight retries, not unguessable.
 */
export function clientId(): string {
  const c: Crypto | undefined = globalThis.crypto;
  if (typeof c?.randomUUID === 'function') return c.randomUUID();

  const bytes = new Uint8Array(16);
  if (typeof c?.getRandomValues === 'function') {
    try {
      c.getRandomValues(bytes);
    } catch {
      // Some older or restricted browsers expose the method but reject it
      // outside a secure context. The id only needs client-side uniqueness.
      for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
    }
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  // Version 4, variant 1, exactly as RFC 4122 lays them out.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

export async function createMatch(
  players: [string, string],
  bestOf = 1,
  playerIds?: [string | null, string | null],
  countries: [string | null, string | null] = [null, null],
  timeLimitMinutes: number | null = null,
  handicap: Handicap | null = null,
  venueId?: string,
  streaming?: false,
  redCount: RedCount = 15,
): Promise<{ id: string; state: ScoreState; reused?: true }> {
  const create = async (): Promise<{ id: string; state: ScoreState; reused?: true }> => {
    if (!preferredPairing()) {
      const previous = typeof window === 'undefined' ? null : loadStoredMatchId();
      if (previous) {
        const { state } = await getMatch(previous);
        if (!state.practice && ['in_progress', 'frame_over'].includes(state.status)) {
          return { id: previous, state, reused: true };
        }
      }
    }
    const result = await json<{ id: string; state: ScoreState; reused?: true }>(
    await fetch(API.matches, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...operatorHeaders() },
      // AI is deliberately opt-in for each match. The match control-bar
      // switch changes this to `assisted` after binding the selected table.
      body: JSON.stringify({
        players,
        countries,
        bestOf,
        redCount,
        mode: 'manual',
        timeLimitMinutes,
        handicap,
        playerIds,
        ...(venueId ? { venueId } : {}),
        ...(streaming === false ? { streaming } : {}),
      }),
    }),
    );
    if (typeof window !== 'undefined' && result.id) window.localStorage.setItem(MATCH_STORAGE_KEY, result.id);
    return result;
  };
  return typeof navigator !== 'undefined' && navigator.locks
    ? navigator.locks.request('aiscoreboard-match-creation', create) : create();
}

export async function getMatch(id: string): Promise<MatchSnapshot> {
  return matchRequest(API.match(id));
}

export function createPracticeMatch(name: string, pots: (BallColor | 'end')[], venueId?: string): Promise<{ id: string; state: ScoreState }> {
  return post(API.matches, {
    players: [name, 'Practice'], practice: true, practicePots: pots,
    mode: 'manual', streaming: false, ...(venueId ? { venueId } : {}),
  });
}

export class MatchRequestTimeoutError extends Error {
  constructor() {
    super('Match request timed out');
    this.name = 'MatchRequestTimeoutError';
  }
}

export function isTransientMatchFailure(error: unknown): boolean {
  return error instanceof TypeError ||
    (error instanceof ApiError && [502, 503, 504].includes(error.status));
}

function retryDelay(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) { reject(signal.reason); return; }
    const abort = () => { clearTimeout(timer); reject(signal.reason); };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', abort);
      resolve();
    }, ms);
    signal.addEventListener('abort', abort, { once: true });
  });
}

async function matchRequest<T>(url: string, options?: RequestInit, retryTransient = false): Promise<T> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 15_000);
  try {
    const request = {
      ...options,
      ...(options?.method && options.method !== 'GET'
        ? { headers: { ...(remoteControlHeaders(url) ?? operatorHeaders()), ...options.headers } } : {}),
      signal: controller.signal,
    };
    for (let attempt = 0; ; attempt += 1) {
      try {
        return await json<T>(await fetch(url, request));
      } catch (error) {
        if (controller.signal.aborted || !retryTransient || attempt >= 2 || !isTransientMatchFailure(error)) throw error;
        await retryDelay(300 * 2 ** attempt, controller.signal);
      }
    }
  } catch (error) {
    if (controller.signal.aborted) throw new MatchRequestTimeoutError();
    throw error;
  } finally {
    clearTimeout(timer);
  }
}

export function createRemoteControl(matchId: string): Promise<{ token: string; expiresAt: string }> {
  return matchRequest(`/api/matches/${encodeURIComponent(matchId)}/remote-control`, {
    method: 'POST', headers: { 'content-type': 'application/json' }, body: '{}',
  });
}

export async function sendEvent(
  id: string,
  event: NewMatchEvent,
): Promise<{ state: ScoreState }> {
  return matchRequest(API.events(id), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    // All attempts reuse this body and id, even if the first reply was lost
    // after the server committed the operation.
    body: JSON.stringify({ ...event, clientId: event.clientId ?? clientId() }),
  }, true);
}

export async function undo(id: string, requestId = clientId()): Promise<{ state: ScoreState }> {
  return matchRequest(API.undo(id), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ clientId: requestId }),
  }, true);
}

async function post<T>(url: string, body: unknown): Promise<T> {
  return matchRequest(url, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
}

/** Without `winner`, the server settles using its current scores. */
export async function endFrame(
  id: string,
  winner?: 0 | 1,
  requestId = clientId(),
): Promise<{ state: ScoreState }> {
  return post(API.endFrame(id), { clientId: requestId, winner });
}

export async function nextFrame(id: string): Promise<{ state: ScoreState }> {
  return post(API.nextFrame(id), {});
}

export async function endMatch(id: string, winner: 0 | 1): Promise<{ state: ScoreState }> {
  return post(API.endMatch(id), { clientId: clientId(), winner });
}

/**
 * Subscribe to live updates, reconnecting with backoff.
 * Returns a disposer.
 */
export function connect(
  matchId: string,
  onMessage: (message: ServerMessage) => void,
  onStatus?: (connected: boolean) => void,
): () => void {
  let socket: WebSocket | null = null;
  let timer: number | undefined;
  let attempt = 0;
  let closed = false;

  const open = (): void => {
    if (closed) return;
    const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
    socket = new WebSocket(`${protocol}//${location.host}${WS_PATH}`);

    socket.onopen = () => {
      attempt = 0;
      onStatus?.(true);
      socket?.send(JSON.stringify({ type: 'subscribe', matchId }));
    };

    socket.onmessage = (raw) => {
      try {
        onMessage(JSON.parse(raw.data as string) as ServerMessage);
      } catch {
        /* ignore malformed frames */
      }
    };

    socket.onclose = () => {
      onStatus?.(false);
      if (closed) return;
      attempt += 1;
      const delay = Math.min(1000 * 2 ** (attempt - 1), 15000);
      timer = window.setTimeout(open, delay);
    };

    socket.onerror = () => socket?.close();
  };

  open();

  return () => {
    closed = true;
    if (timer) window.clearTimeout(timer);
    socket?.close();
  };
}

export function foulAndMiss(id: string, shotId: string, points: number, expectedSeq: number): Promise<{ state: ScoreState }> {
  return post(`/api/matches/${encodeURIComponent(id)}/foul-and-miss`, { shotId, points, expectedSeq });
}

export function resetOverlay(id: string): Promise<{ state: ScoreState }> {
  return post(`/api/matches/${encodeURIComponent(id)}/reset-overlay`, {});
}

/** Return the offender after the existing foul award, without awarding it twice. */
export function restoreAfterFoul(id: string, expectedSeq: number): Promise<{ state: ScoreState }> {
  return post(`/api/matches/${encodeURIComponent(id)}/foul-and-miss`, { afterFoul: true, expectedSeq });
}

export function awardFoul(id: string, points: number, expectedSeq: number): Promise<{ state: ScoreState }> {
  return post(`/api/matches/${encodeURIComponent(id)}/foul-and-miss`, { recordOnly: true, points, expectedSeq });
}
